import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ExclusiveControlService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    fromLocalDate,
    PersonsOrganisationDto,
    ProjectDto,
    ProjectsService,
    toLocalDate,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type EditableProjectDto = ProjectDto & {
    isEditing: boolean;
};

type ProjectDetailsForm = {
    projectName: FormControl<string>;
    startDate: FormControl<Date | null>;
    endDate: FormControl<Date | null>;
};

type ProjectsForm = {
    addForm: FormGroup<ProjectDetailsForm>;
    editForm: FormGroup<ProjectDetailsForm>;
};

@Component({
    selector: 'projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss']
})
export class ProjectsComponent implements OnInit, OnDestroy {
    @Input() organisation: PersonsOrganisationDto;

    projects: EditableProjectDto[];
    private workTracker = new WorkTracker();
    working = false;
    showArchived = false;

    formGroup: FormGroup<ProjectsForm>;
    editingProjectId: number;
    sortList: { [key: string]: any };
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private logging: FlyFreelyLoggingService,
        private projectsService: ProjectsService,
        protected exclusiveControlService: ExclusiveControlService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.formGroup = new FormGroup<ProjectsForm>({
            addForm: new FormGroup<ProjectDetailsForm>({
                projectName: new FormControl(undefined, [Validators.required]),
                startDate: new FormControl(undefined),
                endDate: new FormControl(undefined)
            }),
            editForm: new FormGroup<ProjectDetailsForm>({
                projectName: new FormControl(undefined, [Validators.required]),
                startDate: new FormControl(undefined),
                endDate: new FormControl(undefined)
            })
        });
    }

    ngOnInit(): void {
        this.fetchData();
        this.sortList = {};
        this.projectsService.change$.subscribe(() => this.fetchData());
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    fetchData() {
        this.projectsService
            .find(this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(projects => {
                this.projects = projects.map(p => ({
                    ...p,
                    isEditing: false
                }));
            });
    }

    createProject() {
        const addValue = this.addForm.value;

        this.projectsService
            .create({
                organisationId: this.organisation.id,
                name: addValue.projectName,
                startDate: toLocalDate(addValue.startDate),
                endDate: toLocalDate(addValue.endDate)
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.logging.success('New project added');
                    this.addForm.reset();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while adding new project: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    onUpdateProject() {
        const editValue = this.editForm.value;
        this.projectsService
            .update(this.editingProjectId, {
                name: editValue.projectName,
                startDate: toLocalDate(editValue.startDate),
                endDate: toLocalDate(editValue.endDate)
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.editForm.reset();
                    this.logging.success('Project updated');
                    this.editingProjectId = null;
                    this.exclusiveControlService.requestUnlock();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error updating project : ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onShowEditMode(editProject: EditableProjectDto) {
        this.editingProjectId = editProject.id;
        this.editForm.setValue({
            projectName: editProject.name,
            startDate: fromLocalDate(editProject.startDate),
            endDate: fromLocalDate(editProject.endDate)
        });
        editProject.isEditing = true;
        this.exclusiveControlService.lock(null);
    }

    onCancelEdit(project: EditableProjectDto) {
        this.editForm.reset();
        project.isEditing = false;
        this.editingProjectId = null;
        this.exclusiveControlService.requestUnlock();
    }

    sort(name: string) {
        if (this.sortList[name]) {
            this.sortList[name] = {
                ascending: !this.sortList[name].ascending
            };
        } else {
            this.sortList = {};
            this.sortList[name] = {
                ascending: true
            };
        }
        this.sortTable(name, this.sortList[name].ascending);
    }

    sortTable(name: string, ascending: boolean) {
        if (ascending) {
            this.entryAsc(name);
        } else {
            this.entryDes(name);
        }
    }

    entryAsc(name: string) {
        this.projects.sort((a, b) => {
            if (a[name] > b[name]) {
                return 1;
            } else if (a[name] < b[name]) {
                return -1;
            } else {
                return 0;
            }
        });
    }

    entryDes(name: string) {
        this.projects.sort((a, b) => {
            if (a[name] > b[name]) {
                return -1;
            } else if (a[name] < b[name]) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    onArchiveProject(project: EditableProjectDto) {
        this.projectsService
            .archive(project.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => (project.archived = results.archived),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while archiving project: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onUnarchiveProject(project: EditableProjectDto) {
        this.projectsService
            .unarchive(project.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => (project.archived = results.archived),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while unarchiving project: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    hasError(controlName: string) {
        const control = this.formGroup?.get(controlName);
        return (control.touched || control.dirty) && control.invalid;
    }

    get addForm(): FormGroup<ProjectDetailsForm> {
        return this.formGroup.get('addForm') as FormGroup<ProjectDetailsForm>;
    }

    get editForm(): FormGroup<ProjectDetailsForm> {
        return this.formGroup.get('editForm') as FormGroup<ProjectDetailsForm>;
    }
}
